import { useEffect, useState } from "react";

/**
 * Feature-detect a touch-primary device (phone / tablet). Used to swap the
 * desktop WASD + pointer-lock-mouse input for the on-screen joystick + drag-
 * orbit camera path. We re-evaluate on `resize` so plugging in a keyboard or
 * rotating an iPad split-screen tab demotes cleanly back to mouse input.
 *
 * Heuristic: `(pointer: coarse)` + `(hover: none)` matchMedia query. That's
 * the modern correct way per CSS Media Queries Level 4 — UA sniffing for
 * "iPhone" misses iPad-as-desktop and Android tablets in desktop mode.
 */
export function useIsTouch(): boolean {
  const [touch, setTouch] = useState<boolean>(() => evaluate());
  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse) and (hover: none)");
    const onChange = (): void => setTouch(evaluate());
    mq.addEventListener("change", onChange);
    window.addEventListener("resize", onChange);
    return () => {
      mq.removeEventListener("change", onChange);
      window.removeEventListener("resize", onChange);
    };
  }, []);
  return touch;
}

/** Detect iOS Safari specifically — MWA cannot run there, so we render a
 *  "Open in Phantom" universal-link CTA instead of the wallet modal. */
export function isIosSafari(): boolean {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua) ||
    (window.navigator.platform === "MacIntel" && window.navigator.maxTouchPoints > 1);
  const isSafari = /^((?!chrome|android|crios|fxios|edgios).)*safari/i.test(ua);
  return isIos && isSafari;
}

function evaluate(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(pointer: coarse) and (hover: none)").matches;
}
